import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, CheckCircle2, ChevronRight, Volume2, VolumeX, RotateCcw, Crosshair } from 'lucide-react';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/woKK9dL7uzSFoZTPEtCLEW/Lumpy%20and%20Bumpy%20in%20Barcelona.mp4';
// PLACEHOLDER: Replace this URL with your attached free music file URL
const BGM_URL = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';
const Header = ({
  step,
  isMuted,
  toggleMute
}: {
  step: number;
  isMuted?: boolean;
  toggleMute?: () => void;
}) => <div className="absolute top-8 left-8 right-8 flex justify-between items-center text-white z-50" data-id="element-0">
    <div className="flex items-center gap-2 bg-slate-950/50 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10 pointer-events-none" data-id="element-1">
      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" data-id="element-2" />
      <span className="text-xs font-bold tracking-widest uppercase text-blue-400" data-id="element-3">
        Race IQ
      </span>
    </div>
    <div className="flex items-center gap-2" data-id="element-4">
      {toggleMute && <button onClick={toggleMute} className="bg-slate-950/50 p-1.5 rounded-full backdrop-blur-md border border-white/10 hover:bg-slate-800 transition-colors cursor-pointer" aria-label={isMuted ? 'Unmute video' : 'Mute video'} data-id="element-5">
          {isMuted ? <VolumeX className="w-4 h-4" data-id="element-6" /> : <Volume2 className="w-4 h-4" data-id="element-7" />}
        </button>}
      <div className="bg-slate-950/50 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10 pointer-events-none" data-id="element-8">
        <span className="text-xs font-bold opacity-90" data-id="element-9">{step}/5</span>
      </div>
    </div>
  </div>;
const VideoCard = ({
  children,
  className = '',
  autoPlay = true,
  time = 0,
  isMuted = true
}: {
  children?: React.ReactNode;
  className?: string;
  autoPlay?: boolean;
  time?: number;
  isMuted?: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      if (!autoPlay) {
        videoRef.current.currentTime = time;
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [autoPlay, time, isMuted]);
  return <div className={`relative m-4 mb-0 rounded-[2rem] overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl shrink-0 ${className}`} data-id="element-10">
      <video ref={videoRef} autoPlay={autoPlay} muted={isMuted} loop playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-11">
        <source src={VIDEO_URL} type="video/mp4" data-id="element-12" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80 pointer-events-none" data-id="element-13" />
      {children}
    </div>;
};
const Screen1 = ({
  onNext,
  isMuted,
  toggleMute
}: {
  onNext: () => void;
  isMuted: boolean;
  toggleMute: () => void;
}) => <div className="h-full flex flex-col bg-slate-950" data-id="element-14">
    <Header step={1} isMuted={isMuted} toggleMute={toggleMute} data-id="element-15" />
    <VideoCard className="flex-1 h-[65%]" autoPlay={true} isMuted={true} data-id="element-16">
      <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQwIj48cmVjdCB3aWR0aD0iMSIgaGVpZ2h0PSI0MCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')] bg-repeat animate-[slide_1s_linear_infinite] pointer-events-none" style={{
      backgroundSize: '100px 100px'
    }} data-id="element-17" />
    </VideoCard>

    <div className="p-6 pb-10 shrink-0 flex flex-col gap-5" data-id="element-18">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-19">
        <div className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-3" data-id="element-20">
          How crews control speed
        </div>
        <h1 className="text-3xl font-extrabold text-white mb-3 leading-[1.1]" data-id="element-21">
          At 50 knots, speed is not just about going faster.
        </h1>
        <p className="text-slate-400 text-sm leading-relaxed" data-id="element-22">
          When the boat is flying, the crew has to manage balance, lift and
          control in every second.
        </p>
      </motion.div>

      <motion.button initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.4
    }} onClick={onNext} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 px-6 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-colors shadow-[0_0_30px_rgba(37,99,235,0.3)] mt-2" data-id="element-23">
        Test speed control <ChevronRight className="w-5 h-5" data-id="element-24" />
      </motion.button>
    </div>
  </div>;
const Screen2 = ({
  onNext,
  isMuted,
  toggleMute
}: {
  onNext: (correct: boolean) => void;
  isMuted: boolean;
  toggleMute: () => void;
}) => {
  const [selected, setSelected] = useState<number | null>(null);
  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    const isCorrect = idx === 1; // B is correct
    setTimeout(() => onNext(isCorrect), 1500);
  };
  return <div className="h-full flex flex-col bg-slate-950" data-id="element-25">
      <Header step={2} isMuted={isMuted} toggleMute={toggleMute} data-id="element-26" />
      <VideoCard className="h-[45%]" autoPlay={true} isMuted={true} data-id="element-27">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none" data-id="element-28">
          <Crosshair className="w-12 h-12 text-blue-500/50 animate-pulse" data-id="element-29" />
        </motion.div>
      </VideoCard>

      <div className="p-6 flex-1 flex flex-col" data-id="element-30">
        <h2 className="text-2xl font-bold text-white mb-3" data-id="element-31">
          What is the crew really controlling when the AC75 is flying?
        </h2>
        <p className="text-slate-400 text-sm mb-6" data-id="element-32">
          Speed comes from staying lifted, stable and efficient above the water.
        </p>

        <div className="flex flex-col gap-3 mt-auto mb-4" data-id="element-33">
          {['A. Only the direction of the boat', 'B. Lift, balance and drag', 'C. How hard the boat hits the waves'].map((opt, i) => {
          const isSelected = selected === i;
          const isCorrect = i === 1;
          let btnClass = 'bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800';
          if (selected !== null) {
            if (isSelected && isCorrect) btnClass = 'bg-blue-600/20 border-blue-500 text-white';else if (isSelected && !isCorrect) btnClass = 'bg-red-500/20 border-red-500 text-white';else if (!isSelected && isCorrect) btnClass = 'bg-blue-600/20 border-blue-500 text-white opacity-50';else btnClass = 'bg-slate-900 border-slate-800 text-slate-500 opacity-50';
          }
          return <button key={i} onClick={() => handleSelect(i)} className={`text-left p-4 rounded-2xl border-2 transition-all duration-300 ${btnClass}`} data-id="element-34">
                <div className="flex justify-between items-center" data-id="element-35">
                  <span className="font-bold text-sm" data-id="element-36">{opt}</span>
                  {isSelected && isCorrect && <CheckCircle2 className="text-blue-500 w-5 h-5 shrink-0 ml-2" data-id="element-37" />}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};
const Screen3 = ({
  onNext,
  wasCorrect,
  isMuted,
  toggleMute
}: {
  onNext: () => void;
  wasCorrect: boolean;
  isMuted: boolean;
  toggleMute: () => void;
}) => <div className="h-full flex flex-col bg-slate-950" data-id="element-38">
    <Header step={3} isMuted={isMuted} toggleMute={toggleMute} data-id="element-39" />
    <VideoCard className="h-[45%]" autoPlay={true} isMuted={true} data-id="element-40">
      {/* Control points overlay */}
      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.3
    }} className="absolute top-[40%] left-[30%] w-3 h-3 bg-blue-500 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.8)] pointer-events-none" data-id="element-41" />
      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.6
    }} className="absolute top-[60%] right-[40%] w-3 h-3 bg-blue-500 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.8)] pointer-events-none" data-id="element-42" />
    </VideoCard>

    <div className="p-6 flex-1 flex flex-col" data-id="element-43">
      <h2 className={`text-2xl font-bold mb-4 ${wasCorrect ? 'text-green-400' : 'text-white'}`} data-id="element-44">
        {wasCorrect ? 'Exactly. The crew is managing the conditions that make speed possible.' : 'Not quite. The key is not just steering or pushing harder.'}
      </h2>

      <p className="text-slate-300 text-base leading-relaxed mb-auto" data-id="element-45">
        An AC75 is fastest when it stays lifted and stable. The crew works to
        keep the boat balanced, reduce drag and maintain control as conditions
        change.
      </p>

      <button onClick={onNext} className="w-full bg-white text-slate-950 py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 mt-6 hover:bg-slate-200 transition-colors" data-id="element-46">
        See control points <Play className="w-5 h-5 fill-current" data-id="element-47" />
      </button>
    </div>
  </div>;
const Screen4 = ({
  onNext,
  isMuted,
  toggleMute
}: {
  onNext: () => void;
  isMuted: boolean;
  toggleMute: () => void;
}) => {
  const [showButton, setShowButton] = useState(false);
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 600);
    const t2 = setTimeout(() => setStep(2), 1400);
    const t3 = setTimeout(() => setStep(3), 2200);
    const t4 = setTimeout(() => setStep(4), 3000);
    const t5 = setTimeout(() => setShowButton(true), 4200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, []);
  return <div className="h-full flex flex-col bg-slate-950" data-id="element-48">
      <Header step={4} isMuted={isMuted} toggleMute={toggleMute} data-id="element-49" />
      <VideoCard className="flex-1 h-[60%]" autoPlay={true} isMuted={true} data-id="element-50">
        <AnimatePresence data-id="element-51">
          {step >= 1 && <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="absolute top-[15%] left-6 bg-slate-900/80 backdrop-blur-md border border-slate-700 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg pointer-events-none" data-id="element-52">
              Lift
            </motion.div>}
          {step >= 2 && <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="absolute top-[30%] right-6 bg-slate-900/80 backdrop-blur-md border border-slate-700 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg pointer-events-none" data-id="element-53">
              Balance
            </motion.div>}
          {step >= 3 && <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="absolute top-[45%] left-6 bg-slate-900/80 backdrop-blur-md border border-slate-700 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg pointer-events-none" data-id="element-54">
              Trim
            </motion.div>}
          {step >= 4 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="absolute top-[60%] right-6 bg-blue-600/90 backdrop-blur-md border border-blue-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-[0_0_20px_rgba(37,99,235,0.4)] pointer-events-none" data-id="element-55">
              Drag
            </motion.div>}
        </AnimatePresence>
      </VideoCard>

      <div className="p-6 shrink-0 flex flex-col gap-4" data-id="element-56">
        <div data-id="element-57">
          <h2 className="text-2xl font-bold text-white mb-2" data-id="element-58">
            Speed is controlled before it is seen.
          </h2>
          <p className="text-slate-400 text-sm" data-id="element-59">
            Small adjustments help the boat stay flying: foil control, trim,
            steering and crew timing all affect how efficiently the boat moves.
          </p>
        </div>

        <div className="h-14 mt-2" data-id="element-60">
          <AnimatePresence data-id="element-61">
            {showButton && <motion.button initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} onClick={onNext} className="w-full h-full bg-blue-600 hover:bg-blue-500 text-white rounded-full font-bold text-lg transition-colors" data-id="element-62">
                Pick next detail
              </motion.button>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
};
const Screen5 = ({
  onReset,
  isMuted,
  toggleMute
}: {
  onReset: () => void;
  isMuted: boolean;
  toggleMute: () => void;
}) => {
  const [voted, setVoted] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);
  const options = [{
    label: 'How foils work',
    percent: 45
  }, {
    label: 'How wind decides the race',
    percent: 35
  }, {
    label: 'How teams film the action',
    percent: 20
  }];
  return <div className="h-full relative bg-slate-950 flex flex-col" data-id="element-63">
      <video ref={videoRef} autoPlay loop playsInline className="absolute inset-0 w-full h-full object-cover blur-sm opacity-60 scale-105 pointer-events-none" data-id="element-64">
        <source src={VIDEO_URL} type="video/mp4" data-id="element-65" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/60 to-slate-950 pointer-events-none" data-id="element-66" />

      <Header step={5} isMuted={isMuted} toggleMute={toggleMute} data-id="element-67" />

      <div className="relative z-10 p-6 pt-28 flex flex-col h-full" data-id="element-68">
        <h2 className="text-3xl font-extrabold text-white mb-3 leading-tight" data-id="element-69">
          What should we unlock next?
        </h2>
        <p className="text-slate-400 text-base mb-8" data-id="element-70">
          Choose the next 50-knot detail.
        </p>

        <div className="flex flex-col gap-3 mb-auto" data-id="element-71">
          {options.map((opt, i) => <button key={i} disabled={voted !== null} onClick={() => setVoted(i)} className="relative overflow-hidden w-full text-left p-5 rounded-2xl border border-slate-700 bg-slate-900/60 backdrop-blur-md hover:bg-slate-800 transition-colors disabled:opacity-100 group" data-id="element-72">
              {voted !== null && <motion.div initial={{
            width: 0
          }} animate={{
            width: `${opt.percent}%`
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className={`absolute inset-0 ${voted === i ? 'bg-blue-600/40' : 'bg-slate-700/40'}`} data-id="element-73" />}
              <div className="relative flex justify-between items-center z-10" data-id="element-74">
                <span className={`font-bold ${voted === i ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} data-id="element-75">
                  {opt.label}
                </span>
                {voted !== null && <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.8
            }} className="text-white font-bold" data-id="element-76">
                    {opt.percent}%
                  </motion.span>}
              </div>
            </button>)}
        </div>

        <AnimatePresence data-id="element-77">
          {voted !== null && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1
        }} className="mt-6" data-id="element-78">
              <p className="text-blue-300 text-sm text-center font-medium mb-6" data-id="element-79">
                Every America’s Cup moment has a hidden system behind it.
              </p>
              <button onClick={onReset} className="w-full bg-white text-slate-950 py-4 rounded-full font-bold text-lg hover:bg-slate-200 transition-colors flex items-center justify-center gap-2" data-id="element-80">
                <RotateCcw className="w-5 h-5" data-id="element-81" /> Watch next short
              </button>
            </motion.div>}
        </AnimatePresence>

        <p className="text-center text-slate-600 text-[10px] mt-8 uppercase tracking-wider font-medium" data-id="element-82">
          Source: Official America’s Cup short-form footage
        </p>
      </div>
    </div>;
};
export function App() {
  const [currentScreen, setCurrentScreen] = useState(1);
  const [quizCorrect, setQuizCorrect] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const bgmRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    if (bgmRef.current) {
      if (isMuted || currentScreen === 5) {
        bgmRef.current.pause();
      } else {
        bgmRef.current.play().catch(() => {});
      }
    }
  }, [isMuted, currentScreen]);
  const toggleMute = () => setIsMuted(!isMuted);
  const nextScreen = () => setCurrentScreen(p => Math.min(p + 1, 5));
  const reset = () => {
    setCurrentScreen(1);
    setQuizCorrect(false);
  };
  const handleQuizAnswer = (correct: boolean) => {
    setQuizCorrect(correct);
    nextScreen();
  };
  const screens = [<Screen1 key="1" onNext={nextScreen} isMuted={isMuted} toggleMute={toggleMute} data-id="element-83" />, <Screen2 key="2" onNext={handleQuizAnswer} isMuted={isMuted} toggleMute={toggleMute} data-id="element-84" />, <Screen3 key="3" onNext={nextScreen} wasCorrect={quizCorrect} isMuted={isMuted} toggleMute={toggleMute} data-id="element-85" />, <Screen4 key="4" onNext={nextScreen} isMuted={isMuted} toggleMute={toggleMute} data-id="element-86" />, <Screen5 key="5" onReset={reset} isMuted={isMuted} toggleMute={toggleMute} data-id="element-87" />];
  return <div className="min-h-screen bg-slate-950 flex items-center justify-center font-sans selection:bg-blue-500/30" data-id="element-88">
      <audio ref={bgmRef} src={BGM_URL} loop data-id="element-89" />

      <div className="w-full max-w-md h-[100dvh] sm:h-[850px] sm:max-h-[90vh] bg-slate-950 relative overflow-hidden sm:rounded-[2.5rem] sm:border-[6px] border-slate-800 shadow-[0_0_50px_rgba(0,0,0,0.5)]" data-id="element-90">
        <AnimatePresence initial={false} custom={currentScreen} data-id="element-91">
          <motion.div key={currentScreen} initial={{
          x: '100%',
          opacity: 0.5
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: '-20%',
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute inset-0 shadow-2xl" data-id="element-92">
            {screens[currentScreen - 1]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}